import { HeroIcon } from '@components/HeroIcon'
import { MediaRender } from '@components/MediaRender'
import { Slider } from '@components/Slider'
import { IExtraContent, ILessons, IMedia } from '@dtos/Course'
import { useRudderstack } from '@hooks/useAnalytics'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useExtraContents } from '@hooks/useExtraContents'
import { useLessonExtraContents } from '@hooks/useLessonExtraContents'
import { useThemeParams } from '@hooks/useThemeParams'
import { GestureReponderEvent } from '@tamagui/core'
import { isContentAvailable } from '@utils/contentAvailable'
import { courseExtraContentAvailability } from '@utils/courseExtraContentAvailability'
import { lessonExtraContentAvailability } from '@utils/lessonExtraContentAvailability'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Circle,
  Spinner,
  Text,
  XStack,
  YStack,
  useMedia,
  useWindowDimensions,
} from 'tamagui'
import { ExtraContentCard } from './ExtraContentCard'
import { ExtraContentGhostLoading } from './ExtraContentGhostLoading'
import { ExtraContentLessonSlider } from './ExtraContentLessonSlider'
import { ExtraContentModal } from './ExtraContentModal'
import { LessonTitleLink } from './LessonTitleLink'
import { NotSupported } from './NotSupported'
import { useGetMedia } from '@services/api/graphql/getMedia'
import { useCurrentMedia } from '@hooks/useCurrentMedia'

import { ExtraContentAllModal } from './ExtraContentAllModal'
import { useExtraContentModalCard } from './useExtraContentModalCard'
import { InView } from '@components/IntersectionObserver'
import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IDisableBtns {
  isFirstContent: boolean
  isLastContent: boolean
}

interface IModalExtraMediaProps extends IMedia {
  index?: number | null
  isMain?: boolean
  disableBtns?: IDisableBtns
}

type FileLink = string | null | undefined

export interface IOpenModalMediaProps extends IMedia {
  disableBtns?: IDisableBtns
}

export function ExtraContentContent() {
  const [openExtraContentModal, setOpenExtraContentModal] = useState(false)
  const [lessonsExtraContentsState, setLessonsExtraContentsState] = useState<
    ILessons | undefined
  >()
  const [extraMedia, setExtraMedia] = useState<IModalExtraMediaProps | null>(
    null,
  )
  const { course, isLoading } = useCourse()
  const { enrollment, enrollmentIsLoading } = useEnrollment()
  const {
    extraContents,
    onLoadMore: loadMoreMainExtraContents,
    hasMore: hasMoreExtraContents,
  } = useExtraContents()
  const {
    lessonsExtraContents,
    onLoadMore,
    isLoading: isLoadingLessonExtraContents,
  } = useLessonExtraContents()
  const { data: themeParams, loading: themeParamsIsLoading } = useThemeParams()
  const { track } = useRudderstack()
  const { t } = useTranslation('ExtraContent')
  const media = useMedia()
  const { courseId, enrollmentId } = useCourseRouteParams()
  const { setCurrentMedia } = useCurrentMedia()
  const [openMainModal, setOpenMainModal] = useState(false)
  const [isMainHovered, setIsMainHovered] = useState(false)

  const {
    data: mediaData,
    loading: isLoadingMedia,
    refetch,
  } = useGetMedia({
    courseId: courseId?.toString() ?? '0',
    enrollmentId: enrollmentId?.toString() ?? '0',
    id: extraMedia?.id?.toString() ?? '0',
    enabled: openExtraContentModal && !!extraMedia?.id,
  })

  const { width } = useWindowDimensions()
  const mediaType =
    extraMedia?.fileExt?.toLowerCase() === '.pdf'
      ? '.pdf'
      : extraMedia?.type || ''

  // Must mirror ExtraContentCard's own width so perView/peek are accurate.
  const cardWidth = useMemo(() => {
    if (media.gtMd) return 407
    if (media.gtSm) return 324
    return 242
  }, [media])

  const containerPadding = useMemo(() => {
    return media.gtMd ? 40 : 16
  }, [media.gtMd])

  const slidesSpacing = 24

  const perView = useMemo(() => {
    const totalWidth = width - containerPadding
    const cardWithSpacing = cardWidth + slidesSpacing
    return totalWidth / cardWithSpacing
  }, [width, containerPadding, cardWidth, slidesSpacing])

  const TitlesFontSize = media.gtSm ? '$font-size-md' : '$font-size-sm'
  const someDataIsNull =
    !course || !lessonsExtraContentsState || !extraContents || !enrollment

  useEffect(() => {
    if (lessonsExtraContentsState) return
    setLessonsExtraContentsState(lessonsExtraContents)
  }, [lessonsExtraContentsState, lessonsExtraContents])

  const onLoadMoreLessonsExtraContents = async () => {
    const data = await onLoadMore?.(
      lessonsExtraContentsState?.nodes.length || 0,
    )
    if (data?.lessons) {
      setLessonsExtraContentsState((prevNodes) => {
        const existingIds = new Set((prevNodes?.nodes || []).map((n) => n.id))
        const newNodes = (data?.lessons?.nodes || []).filter(
          (n) => !existingIds.has(n.id),
        )
        return {
          nodes: [...(prevNodes?.nodes || []), ...newNodes],
        }
      })
    }
  }

  useEffect(() => {
    if (!course || !themeParams?.school?.id) return
    track('Members Page Extra Content Opened', {
      school_id: themeParams.school.id,
      product_name: course?.name,
      product_type: course?.kind,
    })
  }, [track, themeParams?.school?.id, course])

  const setExtraContentModal = useCallback(
    (media: IModalExtraMediaProps) => {
      setExtraMedia(media)
      setOpenExtraContentModal(true)
    },
    [setExtraMedia, setOpenExtraContentModal],
  )

  useEffect(() => {
    setCurrentMedia({
      media: extraMedia,
      enrollmentId: Number(enrollmentId),
      courseId: Number(courseId),
    })
  }, [extraMedia, setCurrentMedia, enrollmentId, courseId])

  const setExtraMediaState = (
    media: IModalExtraMediaProps,
    isMain: boolean,
    actualMediaIndex: number,
  ) => {
    if (!extraMedia || !extraContents) return
    const mediaIndex = extraMedia.index ?? 0
    const isFirstContent = media.index === 0
    const isLastContent =
      mediaIndex + actualMediaIndex === extraContents.nodes.length - 1
    setExtraMedia({
      ...media,
      title: media.title,
      type: media.type,
      index: media.index,
      isMain,
      disableBtns: {
        isFirstContent,
        isLastContent,
      },
    })
  }

  const checkIfContentIsAvailable = (node: IExtraContent) =>
    courseExtraContentAvailability(node, enrollment).isAvailable

  const changePreviewContent = (i: number) => {
    if (!extraContents) return
    if (
      extraMedia?.index !== undefined &&
      extraMedia?.index !== null &&
      extraMedia?.index + i >= 0
    ) {
      if (extraMedia?.isMain) {
        const isExtraContentAvailable = checkIfContentIsAvailable(
          extraContents.nodes[extraMedia?.index + i],
        )
        if (
          isExtraContentAvailable &&
          extraMedia?.index + i < extraContents.nodes.length
        ) {
          const extraContent = {
            ...extraContents.nodes[extraMedia?.index + i].media,
            index: extraMedia?.index + i,
          }
          setExtraMediaState(extraContent, true, i)
        } else {
          changePreviewContent(i + i)
        }
      } else {
        if (!lessonsExtraContentsState) return
        const lesson = lessonsExtraContentsState.nodes.find((lesson) => {
          return lesson.extraMedias.find((media) => media.id === extraMedia.id)
        })
        if (!lesson) return
        let nextIndex = extraMedia.index + i
        while (
          nextIndex >= 0 &&
          nextIndex < lesson.extraMedias.length &&
          !lessonExtraContentAvailability(
            lesson,
            lesson.extraMedias[nextIndex].id,
            enrollment,
          ).isAvailable
        ) {
          nextIndex += i
        }
        if (nextIndex >= 0 && nextIndex < lesson.extraMedias.length) {
          setExtraMedia({
            ...lesson.extraMedias[nextIndex],
            index: nextIndex,
            isMain: false,
            disableBtns: {
              isFirstContent: nextIndex === 0,
              isLastContent: nextIndex === lesson.extraMedias.length - 1,
            },
          })
        }
      }

      refetch()
    }
  }

  const handlePressDownload = useCallback(
    (e: GestureReponderEvent, link: FileLink) => {
      if (link) {
        openWebBrowser(link, true)
      }
      e.stopPropagation()
    },
    [],
  )

  const handlePressOpenModal = useCallback(
    (media: IMedia, index: number, isMain: boolean, nodes: number) => {
      const mediaToOpen = {
        ...media,
        id: media.id,
        type: media.type || null,
        fileUrl: media.fileUrl || null,
        fileExt: media.fileExt || null,
        text: media.text || null,
        title: media.title,
        index,
        isMain,
        disableBtns: {
          isFirstContent: index === 0,
          isLastContent: index === nodes - 1,
        },
      }

      setExtraContentModal(mediaToOpen)
    },
    [setExtraContentModal],
  )

  const formattedData = useMemo(() => {
    if (someDataIsNull) return null
    const filteredLessonsExtraContents = lessonsExtraContentsState.nodes.filter(
      (lesson) => {
        const dataAvailable = {
          releaseAt: lesson.releaseAt,
          releaseAfter: lesson.releaseAfter,
          lockAt: lesson.lockAt,
          lockAfter: lesson.lockAfter,
          createdAt: enrollment.activatedAt,
          courseClassRelease: enrollment.courseClassRelease,
          courseClassReleaseOffset: lesson.courseClassReleaseOffset,
        }
        return isContentAvailable(dataAvailable)
      },
    )
    return {
      title: course.name || 'Sem título',
      extraContents,
      lessons: filteredLessonsExtraContents,
    }
  }, [
    someDataIsNull,
    lessonsExtraContentsState,
    course,
    extraContents,
    enrollment,
  ])

  const shouldDisplaySeeAll =
    (formattedData?.extraContents?.nodes?.length ?? 0) > perView

  const {
    widthValue: modalCardWidth,
    minHeight: modalCardMinHeight,
    flexDirection: modalCardFlexDirection,
    iconHeight: modalCardIconHeight,
    iconWidth: modalCardIconWidth,
  } = useExtraContentModalCard()

  const renderMainModalCards = useMemo(() => {
    if (!formattedData?.extraContents) return []
    return formattedData.extraContents.nodes.map(
      (extraContent: IExtraContent, index: number) => {
        const isLast = index === formattedData.extraContents.nodes.length - 1
        const card = (
          <ExtraContentCard
            key={extraContent.id}
            extraContent={extraContent}
            isLessonAvailable={true}
            handlePressDownload={handlePressDownload}
            handlePressOpenModal={() => {
              handlePressOpenModal(
                extraContent.media,
                index,
                true,
                formattedData.extraContents.nodes.length,
              )
              setOpenMainModal(false)
            }}
            withBorder
            widthValue={modalCardWidth}
            minHeight={modalCardMinHeight}
            flexDirection={modalCardFlexDirection}
            iconHeight={modalCardIconHeight}
            iconWidth={modalCardIconWidth}
          />
        )
        if (isLast) {
          return (
            <InView
              key={extraContent.id}
              onChange={(inView: boolean) =>
                inView &&
                loadMoreMainExtraContents?.(
                  formattedData.extraContents.nodes.length,
                )
              }
              style={{
                width: modalCardWidth,
              }}
            >
              {card}
            </InView>
          )
        }
        return card
      },
    )
  }, [
    formattedData?.extraContents,
    handlePressDownload,
    handlePressOpenModal,
    loadMoreMainExtraContents,
    modalCardWidth,
    modalCardMinHeight,
    modalCardFlexDirection,
    modalCardIconHeight,
    modalCardIconWidth,
  ])

  const isLoadingSomething =
    isLoading ||
    !course ||
    !formattedData ||
    themeParamsIsLoading ||
    enrollmentIsLoading ||
    !enrollment

  if (isLoadingSomething) {
    return <ExtraContentGhostLoading />
  }

  const loadMoreMainSlides = () => {
    loadMoreMainExtraContents?.(formattedData.extraContents.nodes.length)
  }

  const hasExtraContents = formattedData?.extraContents.nodes.length > 0
  const hasLessonsExtraContents = formattedData?.lessons.length > 0

  return (
    <>
      <YStack
        testID="extra-content-section"
        onHoverIn={() => setIsMainHovered(true)}
        onHoverOut={() => setIsMainHovered(false)}
      >
        {hasExtraContents && (
          <>
            <XStack
              alignItems="center"
              marginBottom={16}
              testID="extra-content-title"
            >
              <Circle size={40} bg="$bg-surface" marginRight={8}>
                <HeroIcon icon={['fal', 'books']} color="$accent" size={22} />
              </Circle>
              <DefaultSectionTitle
                title={t('extraContentTitle')}
                onButtonPress={() => setOpenMainModal(true)}
                showButton={shouldDisplaySeeAll}
                isHoveringSection={isMainHovered}
              />
            </XStack>
            <YStack marginHorizontal={-containerPadding}>
              <Slider
                slidesTotalNumber={formattedData.extraContents.nodes.length}
                slidesPerView={perView}
                contentInset={containerPadding}
                displaySliderControls={
                  formattedData.extraContents.nodes.length > perView
                }
                loadMoreContent={loadMoreMainSlides}
                hasMore={hasMoreExtraContents}
                sliderTitle={
                  <LessonTitleLink
                    disabled={true}
                    handlePress={() => null}
                    title={formattedData.title}
                  />
                }
                marginBottomToSliderControls="$spacing-inset-xs"
              >
                {formattedData.extraContents.nodes.map(
                  (extraContent: IExtraContent, index: number) => {
                    return (
                      <ExtraContentCard
                        key={extraContent.id}
                        extraContent={extraContent}
                        isLessonAvailable={true}
                        handlePressDownload={handlePressDownload}
                        handlePressOpenModal={() =>
                          handlePressOpenModal(
                            extraContent.media,
                            index,
                            true,
                            formattedData.extraContents.nodes.length,
                          )
                        }
                      />
                    )
                  },
                )}
              </Slider>
            </YStack>
          </>
        )}
      </YStack>
      {hasLessonsExtraContents && (
        <YStack marginTop={40} testID="lessons-extra-content-title">
          <XStack alignItems="center" marginBottom={16}>
            <Circle size={40} bg="$bg-surface" marginRight={8}>
              <HeroIcon icon={['fal', 'book-alt']} color="$accent" size={22} />
            </Circle>
            <Text
              color="$text-secondary"
              ff="$mulishSemiBold"
              fontSize={TitlesFontSize}
            >
              {t('lessonsExtraContentTitle')}
            </Text>
          </XStack>
        </YStack>
      )}

      {formattedData.lessons.map((lesson, index) => {
        return (
          <ExtraContentLessonSlider
            key={lesson.id}
            lesson={lesson}
            perView={perView}
            enrollmentActivatedAt={enrollment?.activatedAt}
            handlePressDownload={handlePressDownload}
            handlePressOpenModal={handlePressOpenModal}
            isLastLesson={index === formattedData.lessons.length - 1}
            loadMoreLessonsExtraContents={onLoadMoreLessonsExtraContents}
            contentInset={containerPadding}
          />
        )
      })}

      {isLoadingLessonExtraContents && (
        <Spinner size="large" color="$spinner-strong" />
      )}

      <ExtraContentModal
        mediaType={mediaType}
        open={openExtraContentModal}
        onClose={() => setOpenExtraContentModal(false)}
        title={extraMedia?.title || ''}
        onChangeContent={(i: number) => changePreviewContent(i)}
        disabledBtns={extraMedia?.disableBtns}
        shouldAdapt={false}
      >
        {extraMedia && (
          <MediaRender
            media={extraMedia}
            closeModal={() => setOpenExtraContentModal(false)}
            location="extraContent"
            fileUrl={mediaData?.media?.fileUrl || undefined}
            isLoading={isLoadingMedia}
          >
            <NotSupported
              media={extraMedia}
              fileUrl={mediaData?.media?.fileUrl}
            />
          </MediaRender>
        )}
      </ExtraContentModal>

      <ExtraContentAllModal
        title={formattedData?.title || ''}
        open={openMainModal}
        onClose={() => setOpenMainModal(false)}
        cards={renderMainModalCards}
      />
    </>
  )
}
